// deps
import { useCallback, useMemo, useRef, useState } from "react";

// internal-deps
import { getBridgeSdk, NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

// apis
import { submitSplitBill } from "@dineout-payment-success-ui/apis/BookingSuccess";

// interfaces
import {
    Contact,
    ISplitBillCommsSuccessData,
    SplitBillApiRequest,
    SplitBillConfirmData,
    SplitBillContactsView,
} from "@dineout-payment-success-ui/interfaces";

// constants
import {
    EMPTY_SPLIT_BILL_CONTACTS,
    SELF_DISPLAY_NAME,
    SELF_IDENTIFIER,
    SPLIT_BILL_CONTACT_PICKER_CONFIG,
    CONTACT_PICKER_TIMEOUT_MS,
} from "@dineout-payment-success-ui/constants";

// utils
import { roundMoney2 } from "@dineout-payment-success-ui/utils/splitBillMoney";
import { buildContactPickerAnalyticsPayload } from "@dineout-payment-success-ui/utils/SplitBillContactPicker";
import { isEmpty } from "@swiggy-private/js-utils";

interface UseSplitBillParams {
    orderId?: string;
    userName?: string;
    userId?: string;
}

export const useSplitBill = ({ orderId, userName, userId }: UseSplitBillParams) => {
    const [splitBillContacts, setSplitBillContacts] = useState<SplitBillContactsView>(EMPTY_SPLIT_BILL_CONTACTS);
    const [showSplitBillHalfCard, setShowSplitBillHalfCard] = useState(false);
    const [splitBillLoading, setSplitBillLoading] = useState(false);
    const [splitBillSuccess, setSplitBillSuccess] = useState(false);
    const [splitBillSuccessData, setSplitBillSuccessData] = useState<ISplitBillCommsSuccessData | null>(null);
    const [currentSplitWidgetState, setCurrentSplitWidgetState] = useState<string>("");
    const [splitBillError, setSplitBillError] = useState<string>("");
    const lastSplitBillRequestRef = useRef<(SplitBillConfirmData & { upiId: string }) | null>(null);

    const selfContact: Contact = useMemo(
        () => ({ name: userName || SELF_DISPLAY_NAME, number: SELF_IDENTIFIER }),
        [userName],
    );

    const contactPickerAnalyticsPayload = useMemo(() => buildContactPickerAnalyticsPayload({ orderId }), [orderId]);

    const accessContacts = useCallback(
        async (preSelectedContacts?: Contact[]): Promise<Contact[]> => {
            try {
                const baseConfig = preSelectedContacts
                    ? {
                          ...SPLIT_BILL_CONTACT_PICKER_CONFIG,
                          preSelectedContacts: preSelectedContacts.map((contact) => ({
                              name: contact.name,
                              number: contact.number,
                          })),
                      }
                    : SPLIT_BILL_CONTACT_PICKER_CONFIG;

                const config = contactPickerAnalyticsPayload
                    ? { ...baseConfig, analyticsPayload: contactPickerAnalyticsPayload }
                    : baseConfig;

                const response = await getBridgeSdk().contactPicker(config, CONTACT_PICKER_TIMEOUT_MS);

                if (!isEmpty(response?.selectedContacts)) {
                    return (response?.selectedContacts ?? []).map((contact) => ({
                        name: contact.name,
                        number: contact.number,
                        avatar: contact.avatar, // base64 image from NativeBridge
                    }));
                }

                return [];
            } catch (error) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.SPLIT_BILL_CONTACT_PICKER_ERROR,
                    errorMessage: error instanceof Error ? error.message : "Unknown error",
                    orderId: orderId ?? "",
                });
                throw new Error("Failed to access contacts");
            }
        },
        [contactPickerAnalyticsPayload, orderId],
    );

    const handleSplitBillClick = useCallback(async () => {
        try {
            const contactList = await accessContacts();

            if (isEmpty(contactList)) {
                return;
            }

            setSplitBillContacts({ self: selfContact, guests: contactList });
            setShowSplitBillHalfCard(true);
        } catch {
            // no-op
        }
    }, [accessContacts, selfContact]);

    const handleAddMoreGuests = useCallback(
        async (onSuccess?: (newCount: number) => void) => {
            try {
                const currentContactsWithoutSelf = splitBillContacts.guests.map(({ name, number }) => ({
                    name,
                    number,
                }));

                // Call NativeBridge with preSelectedContacts (without avatar)
                const contactList = await accessContacts(currentContactsWithoutSelf);

                // Replace guest list with the picker's full selection (no merge with the previous list).
                setSplitBillContacts({ self: selfContact, guests: contactList });
                onSuccess?.(contactList.length + 1); // +1 for self
            } catch (error) {
                // no-op
            }
        },
        [accessContacts, splitBillContacts, selfContact],
    );

    const handleCloseSplitBillHalfCard = useCallback(() => {
        setShowSplitBillHalfCard(false);
        setSplitBillLoading(false);
        setSplitBillSuccessData(null);
        setSplitBillError("");
        lastSplitBillRequestRef.current = null;
    }, []);

    const executeSplitBillSubmit = useCallback(
        async (splitData: SplitBillConfirmData, upiId: string) => {
            if (isEmpty(userName) || isEmpty(orderId) || isEmpty(userId)) {
                setSplitBillError("User information or order ID is missing");
                return;
            }

            setSplitBillLoading(true);
            setSplitBillError("");
            setSplitBillSuccess(false);
            setSplitBillSuccessData(null);

            try {
                // The self user (payer) is identified by SELF_IDENTIFIER
                const payerShareAmount = roundMoney2(splitData.splits[SELF_IDENTIFIER] || 0);

                const payload: SplitBillApiRequest = {
                    orderId: orderId,
                    totalBillAmount: roundMoney2(splitData.billAmount),
                    dineCashAmount: Number(splitData.dineCashAmount || 0),
                    splitDineCash: splitData.splitDineCash,
                    payer: {
                        userId: userId,
                        name: userName,
                        upiId: upiId,
                        payerShareAmount,
                    },
                    splits: splitBillContacts.guests.map((contact) => ({
                        phoneNumber: contact.number,
                        amount: roundMoney2(splitData.splits[contact.number] || 0),
                        name: contact.name,
                    })),
                };

                const successData = await submitSplitBill(payload);

                setSplitBillLoading(false);
                setSplitBillSuccessData(successData);
                setSplitBillSuccess(true);
                setCurrentSplitWidgetState(successData?.currentSplitWidgetState || "");
            } catch (error) {
                setSplitBillLoading(false);
                setSplitBillError(
                    (error as { statusMessage?: string }).statusMessage || "Network error. Please try again.",
                );
            }
        },
        [userName, userId, orderId, splitBillContacts],
    );

    const handleSplitBillConfirm = useCallback(
        async (splitData: SplitBillConfirmData, upiId: string) => {
            lastSplitBillRequestRef.current = { ...splitData, upiId };
            await executeSplitBillSubmit(splitData, upiId);
        },
        [executeSplitBillSubmit],
    );

    const handleRetrySplitBill = useCallback(async () => {
        const last = lastSplitBillRequestRef.current;
        if (!last) {
            setSplitBillError("");
            return;
        }
        const { upiId, ...splitData } = last;
        setSplitBillError("");
        setSplitBillSuccess(false);
        setSplitBillSuccessData(null);
        await executeSplitBillSubmit(splitData, upiId);
    }, [executeSplitBillSubmit]);

    return {
        // State
        splitBillContacts,
        showSplitBillHalfCard,
        splitBillLoading,
        splitBillSuccess,
        splitBillSuccessData,
        currentSplitWidgetState,
        splitBillError,
        // Handlers
        handleSplitBillClick,
        handleAddMoreGuests,
        handleCloseSplitBillHalfCard,
        handleRetrySplitBill,
        handleSplitBillConfirm,
    };
};
